import { useRealtimeManager } from '@/providers/realtime';
import {
  PresenceMembersMap,
  RealtimeChannelKind,
  RealtimeDiagnostics,
} from '@/providers/realtime-manager';
import { useEffect, useRef, useState } from 'react';

type UseRealtimeChannelEventsOptions<TPayload = unknown> = {
  kind: RealtimeChannelKind;
  channelName: string;
  events: string[];
  enabled?: boolean;
  handler: (payload: TPayload, eventName: string) => void;
};

type UseRealtimePresenceChannelOptions = {
  enabled?: boolean;
};

export const useRealtimeChannelEvents = <TPayload = unknown>({
  kind,
  channelName,
  events,
  enabled = true,
  handler,
}: UseRealtimeChannelEventsOptions<TPayload>) => {
  const { manager } = useRealtimeManager();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled || !channelName) {
      return;
    }

    const normalizedEvents = Array.from(
      new Set(events.map((eventName) => eventName.trim()).filter(Boolean)),
    );

    if (normalizedEvents.length === 0) {
      return;
    }

    return manager.subscribeToEvents({
      kind,
      channelName,
      events: normalizedEvents,
      handler: (payload, eventName) => {
        handlerRef.current(payload as TPayload, eventName);
      },
    });
  }, [channelName, enabled, kind, manager, events.join('|')]);
};

export const useRealtimePresenceChannel = (
  channelName: string,
  { enabled = true }: UseRealtimePresenceChannelOptions = {},
) => {
  const { manager } = useRealtimeManager();
  const [members, setMembers] = useState<PresenceMembersMap>(() =>
    channelName ? manager.getPresenceMembers(channelName) : {},
  );

  useEffect(() => {
    if (!enabled || !channelName) {
      setMembers({});
      return;
    }

    setMembers(manager.getPresenceMembers(channelName));

    return manager.subscribeToPresence({
      channelName,
      onMembersChanged: setMembers,
    });
  }, [channelName, enabled, manager]);

  return {
    members,
    here: Object.values(members),
  };
};

export const useRealtimeDiagnostics = () => {
  const { manager } = useRealtimeManager();
  const [diagnostics, setDiagnostics] = useState<RealtimeDiagnostics>(() =>
    manager.getDiagnosticsSnapshot(),
  );

  useEffect(() => {
    return manager.subscribeToDiagnostics(setDiagnostics);
  }, [manager]);

  return diagnostics;
};
